import { createSlice } from "@reduxjs/toolkit";
// redux
import { dispatch } from "src/redux/store";
// utils
import axios, { API_ENDPOINTS } from "src/utils/axios";

// ----------------------------------------------------------------------

const initialState = {
    isLoading: false,
    error: null,
    achAccount: null,
    achVerifyExpired: false,
    feeEnabled: true,
    feeDescription: "$15 transaction fee per deposit · Processing time: 5–7 business days"
};

const slice = createSlice({
    name: "billing",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },

        stopLoading(state) {
            state.isLoading = false;
        },

        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },

        clearError(state) {
            state.error = null;
        },

        setAchAccount(state, action) {
            state.isLoading = false;
            state.achAccount = action.payload;
            state.achVerifyExpired = false;
        },

        setAchVerifyExpired(state) {
            state.achVerifyExpired = true;
        },

        setFeeInfo(state, action) {
            state.feeEnabled = action.payload.feeEnabled;
            state.feeDescription = action.payload.feeDescription;
        }
    }
});

export default slice.reducer;

export const { clearError } = slice.actions;

// ----------------------------------------------------------------------

export function getAchAccount() {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());

            const response = await axios.get(API_ENDPOINTS.billing.getAchAccount);

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                dispatch(slice.actions.setAchAccount(data?.data ?? null));
                return data?.data ?? null;
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
        return null;
    };
}

// ----------------------------------------------------------------------

export function bindAchAccount(request) {
    return async () => {
        try {
            const response = await axios.post(API_ENDPOINTS.billing.bindAchAccount, { ...request });

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                const account = await dispatch(getAchAccount());
                return account ?? true;
            }
            return data?.msg || false;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            return e?.msg || false;
        }
    };
}

// ----------------------------------------------------------------------

// params: { amounts: [0.32, 0.45] } | { descriptor_code: "SM11AA" }
export function verifyAchAccount(params) {
    return async () => {
        try {
            const response = await axios.post(API_ENDPOINTS.billing.verifyAchAccount, params);

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                dispatch(getAchAccount());
                return { success: true };
            }
            return { success: false, code: data?.code, msg: data?.msg };
        } catch (e) {
            const code = e?.code;

            if (code === "PAYMENT_METHOD_VERIFY_TIMEOUT") {
                dispatch(slice.actions.setAchVerifyExpired());
                return { success: false, code };
            }
            if (code === "PAYMENT_METHOD_VERIFY_ALREADY_VERIFIED") {
                dispatch(getAchAccount());
                return { success: true };
            }
            if (
                code === "PAYMENT_METHOD_VERIFY_AMOUNTS_INCORRECT" ||
                code === "PAYMENT_METHOD_VERIFY_DESCRIPTOR_INCORRECT"
            ) {
                return { success: false, code };
            }
            dispatch(getAchAccount());
            return { success: false, code: code || "PAYMENT_METHOD_VERIFY_FAIL", msg: e?.msg };
        }
    };
}

// ----------------------------------------------------------------------

export function deleteAchAccount() {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());

            const response = await axios.post(API_ENDPOINTS.billing.deleteAchAccount);

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                dispatch(slice.actions.setAchAccount(null));
                return true;
            }
            dispatch(slice.actions.stopLoading());
            return data?.msg || false;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            return e?.msg || false;
        }
    };
}

// ----------------------------------------------------------------------

export function submitAchRecharge(request) {
    return async () => {
        try {
            const response = await axios.post(API_ENDPOINTS.billing.submitAchRecharge, { ...request });

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                return { success: true, message: data?.data?.message };
            }
            return data?.msg || false;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            return e?.msg || false;
        }
    };
}

// ----------------------------------------------------------------------

// export function loadDefaultFeeInfo() {
//     return async () => {
//         try {
//             const response = await axios.get(`${API_ENDPOINTS.billing.previewAchRecharge}?amount=100`);
//
//             const { status, data } = response;
//
//             if (status === 200 && data?.code === "ok" && data?.data) {
//                 const { fee_enabled, fee_description } = data.data;
//                 dispatch(slice.actions.setFeeInfo({
//                     feeEnabled: fee_enabled,
//                     feeDescription: fee_description
//                 }));
//             }
//         } catch (e) {
//             // silently fail, keep defaults
//         }
//     };
// }
